import { useRef, useState, type CSSProperties } from 'react';

import { errorText, useStrings } from '../i18n/index.ts';
import { removeAvatar, setBirthDate, setEmail, setName, uploadAvatar, useSession } from '../state/session.ts';
import { Icon } from '../ui/icons.tsx';
import { Card, ErrorBanner, FieldBox, GlassButton, ScreenTitle, Spinner, Toggle } from '../ui/kit.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import '../styles/account.css';

/** Длинная сторона фото после уменьшения: крупнее круга аватара оно нигде не показывается. */
const AVATAR_MAX_SIDE = 600;
const AVATAR_JPEG_QUALITY = 0.85;

function isoDay(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Дата, с которой открывается выбор: двадцать пять лет назад. */
function defaultBirthDate(): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 25);
  return isoDay(date);
}

/** Первая буква имени для круга; без имени рисуется значок человека. */
export function profileInitial(name: string | null | undefined): string | null {
  const first = [...(name ?? '')][0];
  return first ? first.toUpperCase() : null;
}

/** Аватар клиента: фото, иначе первая буква имени, иначе значок человека. */
export function ProfileAvatar({ url, initial, size = 64 }: { url: string | null | undefined; initial: string | null; size?: number }) {
  return (
    <span className="avatar" style={{ '--size': `${size}px` } as CSSProperties}>
      {url ? <img src={url} alt="" draggable={false} /> : initial ? <span className="t-display">{initial}</span> : <Icon name="person" size={size * 0.44} />}
    </span>
  );
}

/** Квадрат по центру снимка, уменьшенный до `maxSide`, в JPEG. */
async function downscaledJpeg(file: Blob, maxSide: number): Promise<Blob | null> {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();

    const side = Math.min(image.naturalWidth, image.naturalHeight);
    if (side === 0) return null;
    const target = Math.round(side * Math.min(1, maxSide / side));
    const canvas = document.createElement('canvas');
    canvas.width = target;
    canvas.height = target;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(image, (image.naturalWidth - side) / 2, (image.naturalHeight - side) / 2, side, side, 0, 0, target, target);
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', AVATAR_JPEG_QUALITY));
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Правка профиля: фото, имя и дата рождения. Все поля необязательны: сохранение с пустым
 * именем убирает его, выключенная дата рождения стирает её из профиля.
 */
export function EditProfileSheet(props: { onClose: () => void }) {
  const strings = useStrings();
  return (
    <Sheet size="large" label={strings.editProfile} onClose={props.onClose}>
      {(close) => <EditProfileForm close={close} />}
    </Sheet>
  );
}

function EditProfileForm({ close }: { close: () => void }) {
  const strings = useStrings();
  const profile = useSession().profile;

  const [name, setNameText] = useState(profile?.name ?? '');
  const [email, setEmailText] = useState(profile?.email ?? '');
  const [hasBirthDate, setHasBirthDate] = useState(Boolean(profile?.birthDate));
  const [birthDate, setBirthDateText] = useState(profile?.birthDate ?? defaultBirthDate());
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const save = async () => {
    if (isSaving || isUploading) return;
    setIsSaving(true);
    setError(null);
    try {
      const trimmed = name.trim();
      if (trimmed !== (profile?.name ?? '')) await setName(trimmed);
      const mail = email.trim();
      if (mail !== (profile?.email ?? '')) await setEmail(mail);
      const iso = hasBirthDate && birthDate ? birthDate : null;
      if (iso !== (profile?.birthDate ?? null)) await setBirthDate(iso);
      close();
    } catch (failure) {
      setError(errorText(strings, failure));
    } finally {
      setIsSaving(false);
    }
  };

  const photoAction = async (action: () => Promise<void>) => {
    setIsUploading(true);
    setError(null);
    try {
      await action();
    } catch (failure) {
      setError(errorText(strings, failure));
    } finally {
      setIsUploading(false);
    }
  };

  const upload = (file: File) =>
    photoAction(async () => {
      const jpeg = await downscaledJpeg(file, AVATAR_MAX_SIDE);
      if (!jpeg) {
        setError(strings.somethingWentWrong);
        return;
      }
      await uploadAvatar(jpeg);
    });

  return (
    <>
      <div className="sheet-header">
        <GlassButton icon="close" label={strings.cancel} onClick={close} />
        {isSaving ? (
          <span className="edit-profile-saving">
            <Spinner />
          </span>
        ) : (
          <GlassButton icon="check" label={strings.save} tone="accent" onClick={() => void save()} />
        )}
      </div>
      <div className="sheet-body">
        <div className="readable sheet-pad stack edit-profile">
          <ScreenTitle text={strings.editProfile} />

          <div className="stack edit-profile-photo">
            <span className="edit-profile-avatar">
              <ProfileAvatar url={profile?.avatarUrl} initial={profileInitial(profile?.name)} size={112} />
              {isUploading && (
                <span className="edit-profile-uploading">
                  <Spinner />
                </span>
              )}
            </span>
            <div className="row">
              <button type="button" className="text-button" disabled={isUploading} onClick={() => fileInput.current?.click()}>
                {strings.changePhoto}
              </button>
              {profile?.avatarUrl && (
                <button type="button" className="text-button danger" disabled={isUploading} onClick={() => void photoAction(removeAvatar)}>
                  {strings.removePhoto}
                </button>
              )}
            </div>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                // Тот же файл можно выбрать снова: поле очищается сразу.
                event.target.value = '';
                if (file) void upload(file);
              }}
            />
          </div>

          <Card>
            <form
              className="stack edit-profile-card"
              onSubmit={(event) => {
                event.preventDefault();
                void save();
              }}
            >
              <FieldBox label={strings.yourName} muted>
                <input
                  type="text"
                  autoComplete="given-name"
                  enterKeyHint="done"
                  placeholder={strings.yourNamePlaceholder}
                  value={name}
                  onChange={(event) => setNameText(event.target.value)}
                />
              </FieldBox>
              <p className="t-caption t-muted">{strings.nameOptionalHint}</p>
              <FieldBox label={strings.emailForReceipts} muted>
                <input
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  enterKeyHint="done"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(event) => setEmailText(event.target.value)}
                />
              </FieldBox>
              <p className="t-caption t-muted">{strings.emailForReceiptsHint}</p>
            </form>
          </Card>

          <Card>
            <div className="stack edit-profile-card">
              <div className="row edit-profile-toggle">
                <span className="grow">{strings.birthDate}</span>
                <Toggle checked={hasBirthDate} label={strings.birthDate} onChange={setHasBirthDate} />
              </div>
              {hasBirthDate && (
                <FieldBox muted>
                  <input
                    type="date"
                    aria-label={strings.birthDate}
                    max={isoDay(new Date())}
                    value={birthDate}
                    onChange={(event) => setBirthDateText(event.target.value)}
                  />
                </FieldBox>
              )}
              <p className="t-caption t-muted">{strings.birthDateHint}</p>
            </div>
          </Card>

          {error && <ErrorBanner text={error} />}
        </div>
      </div>
    </>
  );
}
